/**
 * Valorización del inventario y margen de lo vendido.
 *
 * Responde dos preguntas que antes no tenían respuesta: cuánto vale lo que hay
 * en el anaquel (al costo, no al precio de venta) y cuánto se ganó de verdad
 * con lo despachado.
 *
 * El método es PEPS: el costo de lo que sale es el de las unidades más
 * antiguas. Se declara en pantalla porque no es un detalle técnico — es una
 * decisión contable que no debe cambiarse a mitad de año.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Coins, TrendingUp } from "lucide-react";
import { getResumenVentas, getValorizacion } from "../api/farmacia";
import { Card, CardBody, CardHeader } from "../components/ui";

const soles = (v: string | number | null | undefined) =>
  `S/ ${Number(v ?? 0).toFixed(2)}`;

const PERIODOS = [
  { id: 7, label: "7 días" },
  { id: 30, label: "30 días" },
  { id: 90, label: "90 días" },
];

export function FarmaciaValorizacionPage() {
  const [dias, setDias] = useState(30);

  const { data: val, isLoading } = useQuery({
    queryKey: ["farmacia", "valorizacion"],
    queryFn: () => getValorizacion(),
  });
  const { data: resumen } = useQuery({
    queryKey: ["farmacia", "resumen-ventas", dias],
    queryFn: () =>
      getResumenVentas({
        desde: new Date(Date.now() - dias * 86_400_000).toISOString(),
      }),
  });

  const costo = Number(val?.total_costo ?? 0);
  const venta = Number(val?.total_venta ?? 0);
  // Lo que se ganaría si se vendiera todo lo que hay. No es utilidad todavía:
  // es el margen que está dormido en el anaquel.
  const margenPotencial = venta - costo;

  const ingresos = Number(resumen?.ingresos ?? 0);
  const costoVendido = Number(resumen?.costo ?? 0);
  const margen = Number(resumen?.margen ?? 0);
  const porcentaje = ingresos > 0 ? (margen / ingresos) * 100 : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-slate-900">
          <Coins className="h-6 w-6 text-[color:var(--color-link)]" />
          Valorización
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Cuánto vale el inventario y cuánto deja lo que se vende. Método{" "}
          <strong>{val?.metodo ?? "PEPS"}</strong> — primeras entradas, primeras
          salidas.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Tarjeta
          titulo="Inventario al costo"
          valor={soles(costo)}
          detalle={`${val?.total_unidades ?? 0} unidades en stock`}
        />
        <Tarjeta
          titulo="A precio de venta"
          valor={soles(venta)}
          detalle="Si se vendiera todo lo que hay"
        />
        <Tarjeta
          titulo="Margen dormido"
          valor={soles(margenPotencial)}
          detalle="Lo que dejaría ese inventario"
        />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="flex items-center gap-2 font-semibold text-slate-900">
                <TrendingUp className="h-4 w-4 text-emerald-600" />
                Lo vendido
              </h2>
              <p className="mt-0.5 text-xs text-slate-500">
                Ingresos menos el costo real de lo que salió, no menos el último
                precio de compra.
              </p>
            </div>
            <div className="inline-flex rounded-lg border border-slate-200 p-0.5 text-sm">
              {PERIODOS.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setDias(p.id)}
                  className={`rounded-md px-3 py-1.5 font-medium transition ${
                    dias === p.id
                      ? "bg-[color:var(--color-link)] text-white"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Dato k="Ventas" v={String(resumen?.ventas ?? 0)} />
            <Dato k="Ingresos" v={soles(ingresos)} />
            <Dato k="Costo (PEPS)" v={soles(costoVendido)} />
            <Dato
              k="Margen"
              v={soles(margen)}
              extra={ingresos > 0 ? `${porcentaje.toFixed(1)}%` : undefined}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-slate-900">Detalle por producto</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            De mayor a menor valor: arriba está la plata inmovilizada.
          </p>
        </CardHeader>
        <CardBody className="p-0">
          {isLoading && (
            <p className="px-6 py-8 text-center text-sm text-slate-400">Cargando…</p>
          )}
          {!isLoading && (val?.items.length ?? 0) === 0 && (
            <p className="px-6 py-10 text-center text-sm text-slate-500">
              Sin existencias que valorizar.
            </p>
          )}
          {(val?.items.length ?? 0) > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                  <tr>
                    <th className="px-6 py-2 font-medium">Producto</th>
                    <th className="px-3 py-2 text-right font-medium">Stock</th>
                    <th className="px-3 py-2 text-right font-medium">Costo prom.</th>
                    <th className="px-3 py-2 text-right font-medium">Valor al costo</th>
                    <th className="px-3 py-2 text-right font-medium">Precio</th>
                    <th className="px-6 py-2 text-right font-medium">Valor de venta</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {val?.items.map((i) => (
                    <tr key={i.producto_id} className="hover:bg-slate-50/60">
                      <td className="px-6 py-2">
                        <span className="text-slate-900">{i.producto_nombre}</span>
                        <span className="ml-2 font-mono text-xs text-slate-400">
                          {i.codigo}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{i.cantidad}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">
                        {`S/ ${Number(i.costo_promedio).toFixed(4)}`}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-slate-900">
                        {soles(i.valor_costo)}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-500">
                        {soles(i.precio_venta)}
                      </td>
                      <td className="px-6 py-2 text-right font-mono text-slate-700">
                        {soles(i.valor_venta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-slate-200 text-sm font-semibold">
                  <tr>
                    <td className="px-6 py-2 text-slate-900">Total</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {val?.total_unidades}
                    </td>
                    <td />
                    <td className="px-3 py-2 text-right font-mono text-slate-900">
                      {soles(costo)}
                    </td>
                    <td />
                    <td className="px-6 py-2 text-right font-mono text-slate-900">
                      {soles(venta)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Tarjeta({
  titulo,
  valor,
  detalle,
}: {
  titulo: string;
  valor: string;
  detalle: string;
}) {
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="text-xs uppercase tracking-wide text-slate-400">{titulo}</p>
      <p className="mt-1 font-mono text-2xl font-bold text-slate-900">{valor}</p>
      <p className="mt-0.5 text-xs text-slate-500">{detalle}</p>
    </div>
  );
}

function Dato({ k, v, extra }: { k: string; v: string; extra?: string }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-slate-400">{k}</p>
      <p className="font-mono text-lg font-semibold text-slate-900">
        {v}
        {extra && <span className="ml-2 text-sm text-emerald-700">{extra}</span>}
      </p>
    </div>
  );
}
